import { getApiBaseUrl } from "../utils/ssoProvider";
// axiosInstance.js
import axios from "axios";
import { getRefreshToken } from "./login"; // Adjust the import based on your project structure
import { getStoredValue } from "./logics";
import store from "../redux/store";
import { setSessionExpired } from "../redux/sessionSlice";
import { createAuthExpiredError } from "./authExpiredError";
import { reportRequestWhileExpired } from "../utils/sessionTelemetry";

const API_URL = getApiBaseUrl();

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Ensures cookies are sent with requests
});

// Request interceptor to add the Authorization header to each request
axiosInstance.interceptors.request.use(
  config => {
    // Once the session is known to be dead, don't send authenticated requests at all. The expired
    // screen is only the UX; this is the protection - it holds even if something in the browser
    // tampers with the page. Sign-in and token refresh use plain axios, so they are unaffected.
    if (store.getState()?.session?.isSessionExpired) {
      reportRequestWhileExpired(config);
      return Promise.reject(createAuthExpiredError(new Error("Session expired - request not sent")));
    }

    const token = getStoredValue("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 Unauthorized errors
axiosInstance.interceptors.response.use(
  response => {
    // If the response is successful, simply return it
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (error?.response && error?.response?.status === 502) {
      // Redirect to the error page with the current page state
      const lastPage = window.location.pathname;
      window.location.href = `/502-error?lastPage=${encodeURIComponent(lastPage)}`;
      return Promise.reject(error);
    }

    // ServicePoints endpoints: fail silently for any error
    const SP_SILENT_PATHS = ["/servicepoints/order-details", "/servicepoints/get-client-details"];
    if (SP_SILENT_PATHS.some(path => (error.config?.url || "").includes(path))) {
      console.warn("[ServicePoints] Request failed (suppressed):", error);
      return Promise.resolve({
        data: null,
        status: error.response?.status ?? 0,
        statusText: error.response?.statusText ?? "",
        headers: error.response?.headers ?? {},
        config: error.config,
        request: error.request,
      });
    }

    // Handle all 401 Unauthorized responses
    if (error.response && error.response.status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        let refreshSuccess;
        try {
          refreshSuccess = await getRefreshToken();
        } catch (_) {
          // Refresh couldn't reach a verdict (aborted/offline/5xx): the session isn't known to be
          // dead, so surface this request's own failure rather than an auth-expired one.
          return Promise.reject(error);
        }
        if (refreshSuccess) {
          const newToken = getStoredValue("token");
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest); // Retry the original request
        }
        // Refresh failed — getRefreshToken already dispatched setSessionExpired
      } else {
        // The retried request itself returned 401; session is definitively expired
        store.dispatch(setSessionExpired(true));
      }

      // Reject with a MARKED error rather than returning a never-settling
      // promise. The session-expired screen (SessionExpiredGate) is still the single UX entry point for
      // auth expiry - callers check isAuthExpiredError() and stay quiet - but a
      // rejection lets their catch/finally run, so a component that set a
      // loading flag before the call can clear it and render something instead
      // of sitting on a blank box or a spinner forever.
      return Promise.reject(createAuthExpiredError(error));
    }

    // Propagate all non-401 errors to the caller
    return Promise.reject(error);
  }
);

export default axiosInstance;
